import { ElementType, ReactNode, RefObject, useCallback } from 'react';
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import rehypeExternalLinks from "rehype-external-links";
import DOMPurify from 'dompurify';
import { mergeRefs } from '@/utils/merge-refs';

import './typography-item.scss';

type TTagName =
	| 'h1'
	| 'h2'
	| 'h3'
	| 'h4'
	| 'h5'
	| 'h6'
	| 'p'
	| 'span'
	| 'div'
	| 'a'
	| 'time';

const ALLOWED_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'div', 'a', 'time'];

// The Sanitizer API is still not available in safari, but it's faster than DomPurify and more internally robust
// https://developer.mozilla.org/en-US/docs/Web/API/Sanitizer
declare global {
	interface Window {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		Sanitizer?: any;
	}
}

const sanitizer = window && ('Sanitizer' in window) && new window.Sanitizer({
	elements: ALLOWED_TAGS
});

interface ITypographyItemProps {
	children?: ReactNode;
	tagName?: TTagName;
	className?: string;
	error?: boolean;
	/** TypographyItem will sanitize automatically using setHTML, but falling back to DomPurify if setHTML is unavailable */
	dangerouslySetInnerHTML?: { __html: string };
	ref?: RefObject<HTMLElement | null> | null;
	[key: string]: any;
}

export const TypographyItem = ({
	children,
	tagName = 'div',
	className = '',
	error,
	dangerouslySetInnerHTML,
	ref,
	...rest
}: ITypographyItemProps): JSX.Element => {
	const TagName = `${tagName}` as ElementType;

	const handleRef = useCallback(r => {
		mergeRefs(r, ref);
		let htmlToSet = dangerouslySetInnerHTML?.__html;
		if (!r) return;
		const _ref = r as HTMLElement;
		if (htmlToSet === undefined && typeof children === 'string') {
			/*
				A lot of our editor components use contenteditable attributes,
				which does not strip formatting when user's copy/paste, which means it may end up rendering unexpected html strings on attendee pages
				This fallback means we'll never render html strings on attendee pages anymore
			*/
			htmlToSet = children;
		}
		if (!htmlToSet) return undefined;
		if (sanitizer && 'setHTML' in _ref && typeof _ref.setHTML === 'function') {
			_ref.setHTML(htmlToSet, { sanitizer });
		} else {
			_ref.innerHTML = DOMPurify.sanitize(htmlToSet, { ALLOWED_TAGS });
		}
	}, [children, dangerouslySetInnerHTML?.__html, ref]);

	return (
		<TagName
			className={`${className} ${error ? 'typography-item-error' : ''}`}
			ref={handleRef}
			{...rest}
		>
			{children}
		</TagName>
	);
};


// moving our call to react markdown here so as it's capabilities grow 
// it only has to be edited in a single location rather than scattered throughout the app
// plugins:
// remark-breaks translates \n into </br >, 
// remark-gfm brings in extra github features including easy links
interface IMarkdownItemProps {
	children: string;
	className?: string;
	[key: string]: any;
}

export const MarkdownItem = ({
	children,
	className = '',
	...rest
}: IMarkdownItemProps): JSX.Element => {
	return (
		<ReactMarkdown
			className={className}
			remarkPlugins={[remarkBreaks, remarkGfm]}
			rehypePlugins={[[rehypeExternalLinks, { target: "_blank", rel: ['nofollow', 'noopener', "noreferrer"] }]]}
			components={{
				// strikethrough markdown is used for underline text so we have to override the styling to show as underline
				// eslint-disable-next-line react/display-name
				del: ({ ...props }) => <span style={{ textDecorationLine: 'underline' }} {...props} />
			}}
			{...rest}>
			{children}
		</ReactMarkdown>
	);
};
